/**
 * FrozenDataBadge — wires the admin data-source switch (utils/dataSource.js)
 * to the signed-in user, and shows a fixed "Frozen data" badge next to the
 * admin pill whenever an admin is browsing the May 21 snapshot, so frozen
 * results are never mistaken for live ones. Clicking it returns to live data.
 */
import React from 'react';
import { Text, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../firebaseConfig';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { configureDataSource, isFrozenDataActive, setStoredDataSource, FROZEN_LABEL } from '../utils/dataSource';

export const FROZEN_COLOR = '#38BDF8';

/** Switch source and reload so every screen and cache starts from that source. */
export function switchDataSource(source) {
  setStoredDataSource(source);
  if (Platform.OS === 'web' && typeof window !== 'undefined') window.location.reload();
}

// Runs during render so the very first requests after a reload already know
// whether the user is an admin (effects run after children's first fetches).
function useConfigureDataSource(isAdmin, known) {
  configureDataSource({
    isAdmin,
    known,
    getIdToken: () => (auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null)),
  });
}

export default function FrozenDataBadge() {
  const { isAdmin, userDataLoaded } = useNavigation();
  const { user, loading: authLoading } = useAuth();
  // Settled once auth resolved signed-out, or the signed-in user's doc loaded.
  const statusKnown = (!authLoading && !user) || (!!user && !!userDataLoaded);
  useConfigureDataSource(isAdmin && !!user, statusKnown);

  if (Platform.OS !== 'web' || !isAdmin || !user || !isFrozenDataActive()) return null;

  return (
    <Pressable
      onPress={() => switchDataSource('live')}
      accessibilityRole="button"
      accessibilityLabel="Frozen data is on. Switch to live data"
      style={({ hovered }) => [styles.badge, hovered && styles.badgeHovered]}
    >
      {({ hovered }) => (
        <>
          <Ionicons name="snow-outline" size={13} color={FROZEN_COLOR} />
          <Text style={styles.text}>{FROZEN_LABEL.toUpperCase()}</Text>
          <Text style={styles.hint}>{hovered ? 'Switch to live' : 'admin only'}</Text>
        </>
      )}
    </Pressable>
  );
}

const styles = {
  badge: {
    // Clear of the admin pill's expanded width (16 + 130 + 8).
    position: 'fixed', bottom: 16, left: 154, zIndex: 99999,
    flexDirection: 'row', alignItems: 'center', gap: 6, height: 28, paddingHorizontal: 10,
    borderRadius: 8, borderWidth: 1, borderColor: FROZEN_COLOR + '80',
    backgroundColor: 'rgba(8,47,73,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    transition: 'background-color 160ms ease, transform 160ms ease',
  },
  badgeHovered: { backgroundColor: 'rgba(12,74,110,0.96)', transform: [{ translateY: -1 }] },
  text: { color: FROZEN_COLOR, fontSize: 10, fontWeight: '800', letterSpacing: 0.4 },
  hint: { color: 'rgba(255,255,255,0.45)', fontSize: 9, fontWeight: '500' },
};
